import {
  ExclamationCircleFilled,
  PhoneOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { Checkbox, Form, Radio } from "antd";
import { httpCreators, useLoadingSelector } from "@ppvrbh/merchant-frontend-core";
import Lottie from "lottie-react";
import React, { ChangeEvent, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  RBHBox,
  RBHButton,
  RBHImage,
  RBHRadio,
  RBHText,
  RBHTextInput,
} from "../../../shared/components";
import { mainCreators } from "../../main-action";
import { MAIN_STATE_KEY } from "../../main-reducer";
import invitationHero from "../../../shared/resources/image/invitation-header.png";
import invitationScbHero from "../../../shared/resources/image/invitation-scb-header.png";
import successAnimation from "../../../shared/resources/animation/success.json";
import { invitationCreators } from "./redux/invitation-action";
import { INVITATION_REQUEST_ID_LIST } from "./redux/invitation-constant";
import {
  InvitationFormData,
  InvitationStep,
  invitationStateSelector,
  INVITATION_STATE_KEY,
} from "./redux/invitation-reducer";
import { GetTncDetailHandlerResp, GetTncStatusHandlerResp, invitationSagas } from "./redux/invitation-saga";
import { InvitationService } from "./redux/invitation-service";
import styles from "./styles";
import "./styles.css";
import { Colors } from "../../../shared/theme";
import { useLocation } from "react-router";

const DEFAULT_AVAILABLE_TIME = "09:00 - 12:00";

const defaultInvitationFormData: InvitationFormData = {
  contactName: "",
  contactNumber: "",
  availableTime: DEFAULT_AVAILABLE_TIME,
  token: "",
};

export const InvitationScreen: React.FC<Props> = () => {
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const invitationStep = useSelector(
    invitationStateSelector(INVITATION_STATE_KEY.STEP),
  ) as InvitationStep;
  const invitationFormData = useSelector(
    invitationStateSelector(INVITATION_STATE_KEY.FORM),
  ) as InvitationFormData;
  const isSubmitting = useLoadingSelector(
    INVITATION_REQUEST_ID_LIST.POST_INVITATION_REGISTRATION,
  );
  const { search } = useLocation();
  const { channel, token } = useMemo(() => {
    const queryParams = new URLSearchParams(search);
    return {
      channel: queryParams.get('channel') ?? undefined,
      token: queryParams.get('token')
    }
  }, [search])


  const [selectedTime, setSelectedTime] = useState(DEFAULT_AVAILABLE_TIME);
  const [formInvalid, setFormInvalid] = useState(true);
  const [focusedField, setFocusedField] = useState<string | null>(null);
  const [agreedTerms, setAgreedTerms] = useState(false);
  const [termsReachedEnd, setTermsReachedEnd] = useState(false);
  const [scrollBottom, setScrollBottom] = useState(false);
  const [editedFields, setEditedFields] = useState({
    contactName: false,
    contactNumber: false,
  });
  const termsScrollRef = useRef<HTMLDivElement | null>(null);
  const [inputValues, setInputValues] = useState({
    contactName: "",
    contactNumber: "",
  });
  const [tncStatus, setTncStatus] = useState<GetTncStatusHandlerResp>();
  const [tncDetail, setTncDetail] = useState<GetTncDetailHandlerResp>();

  useEffect(() => {
    dispatch(mainCreators.setMainState(MAIN_STATE_KEY.HEADER_MENU, 0));
    window.scrollTo({ top: 0, behavior: "smooth" });

    const handleGetTncSuccess = (data: GetTncStatusHandlerResp) => {
      setTncStatus(data);
    }

    dispatch(
      httpCreators.request({
        id: INVITATION_REQUEST_ID_LIST.GET_TNC_STATUS,
        sagaHandler: invitationSagas.getTncStatusHandler,
        callbackParams: {
          onSuccess: handleGetTncSuccess,
        },
        service: InvitationService.getTncStatus,
        serviceParams: {
          token,
          channel
        },
      })
    )

    dispatch(
      invitationCreators.setInvitationState(INVITATION_STATE_KEY.FORM, {
        ...defaultInvitationFormData,
        token: token ?? "",
      }),
    );
  }, [dispatch, token]);

  useEffect(() => {
    if (!tncStatus?.tncUrl) {
      return;
    }
  
    dispatch(
      httpCreators.request({
        id: INVITATION_REQUEST_ID_LIST.GET_TNC_DETAIL,
        sagaHandler: invitationSagas.getTncDetailHandler,
        callbackParams: {
          onSuccess: (data: GetTncDetailHandlerResp) => setTncDetail(data),
        },
        service: InvitationService.getTncDetail,
        serviceParams: {
          url: tncStatus.tncUrl,
        },
      })
    )
  }, [tncStatus])

  useEffect(() => {
    form.setFieldsValue({
      contactName: invitationFormData.contactName,
      contactNumber: invitationFormData.contactNumber,
      availableTime: invitationFormData.availableTime || DEFAULT_AVAILABLE_TIME,
    });
    setSelectedTime(invitationFormData.availableTime || DEFAULT_AVAILABLE_TIME);
    setInputValues({
      contactName: invitationFormData.contactName || "",
      contactNumber: invitationFormData.contactNumber || "",
    });
    updateSubmitState(invitationFormData);
  }, [form, invitationFormData]);

  useEffect(() => {
    if (invitationStep !== "terms") {
      setAgreedTerms(false);
      setTermsReachedEnd(false);
      return;
    }

    const scrollElement = termsScrollRef.current;
    if (!scrollElement) {
      return;
    }

    const reachedBottom =
      scrollElement.scrollTop + scrollElement.clientHeight >=
      scrollElement.scrollHeight - 8;
    const hasScrollableContent =
      scrollElement.scrollHeight > scrollElement.clientHeight + 8;

    setTermsReachedEnd(!hasScrollableContent || reachedBottom);
  }, [invitationStep]);

  const isValidPhoneNumber = (phone: string) => /^0\d{7,8}$/.test(phone);

  const updateSubmitState = (nextValues?: any) => {
    const values = nextValues || form.getFieldsValue();
    const hasContactName = !!values.contactName?.trim();
    const isPhoneValid = isValidPhoneNumber(values.contactNumber || "");
    const hasAvailableTime = !!values.availableTime;

    setFormInvalid(!(hasContactName && isPhoneValid && hasAvailableTime));
  };

  const handleValuesChange = (changedValues: any, allValues: any) => {
    setEditedFields((prevState) => ({
      ...prevState,
      ...(Object.prototype.hasOwnProperty.call(changedValues, "contactName")
        ? { contactName: true }
        : {}),
      ...(Object.prototype.hasOwnProperty.call(changedValues, "contactNumber")
        ? { contactNumber: true }
        : {}),
    }));
    setInputValues({
      contactName: allValues.contactName || "",
      contactNumber: allValues.contactNumber || "",
    });
    updateSubmitState(allValues);
  };

  const handleTimeChange = (event: any) => {
    const value = event?.target?.value;
    setSelectedTime(value);
    form.setFieldsValue({
      availableTime: value,
    });
    updateSubmitState({
      ...form.getFieldsValue(),
      availableTime: value,
    });
  };

  const handleFormSubmit = () => {
    const formValues = form.getFieldsValue();
    const values: InvitationFormData = {
      contactName: formValues.contactName || "",
      contactNumber: formValues.contactNumber || "",
      availableTime: selectedTime,
      token: invitationFormData.token || "",
    };

    dispatch(
      invitationCreators.setInvitationState(INVITATION_STATE_KEY.FORM, values),
    );
    dispatch(
      invitationCreators.setInvitationState(INVITATION_STATE_KEY.STEP, "terms"),
    );
  };

  const resetFormData = () => {
    const formData: InvitationFormData = {
      ...defaultInvitationFormData,
      token: invitationFormData.token || "",
    };

    setAgreedTerms(false);
    setEditedFields({
      contactName: false,
      contactNumber: false,
    });
    setFocusedField(null);
    setInputValues({
      contactName: "",
      contactNumber: "",
    });
    setSelectedTime(DEFAULT_AVAILABLE_TIME);
    form.resetFields();
    form.setFieldsValue(formData);
    updateSubmitState(formData);
    dispatch(
      invitationCreators.setInvitationState(
        INVITATION_STATE_KEY.FORM,
        formData,
      ),
    );
  };

  const handleInvitationSuccess = () => {
    dispatch(
      invitationCreators.setInvitationState(
        INVITATION_STATE_KEY.STEP,
        "success",
      ),
    );

    resetFormData();
  };

  const handleAcceptTerms = () => {
    dispatch(
      httpCreators.request({
        id: INVITATION_REQUEST_ID_LIST.POST_INVITATION_REGISTRATION,
        sagaHandler: invitationSagas.postInvitationRegistrationHandler,
        callbackParams: {
          onSuccess: handleInvitationSuccess,
        },
        service: InvitationService.postInvitationRegistration,
        serviceParams: {
          ...invitationFormData,
          availableTime:
            invitationFormData.availableTime || DEFAULT_AVAILABLE_TIME,
          tncVersion: tncDetail?.version,
          channel,
        },
      }),
    );
  };

  const timeOptions = ["09:00 - 12:00", "13:00 - 18:00"];
  const shouldFloatName =
    focusedField === "contactName" || !!inputValues.contactName;
  const shouldFloatPhone =
    focusedField === "contactNumber" || !!inputValues.contactNumber;
  const hasNameError =
    editedFields.contactName && !inputValues.contactName.trim();
  const hasPhoneError =
    editedFields.contactNumber &&
    (!inputValues.contactNumber.trim() || !isValidPhoneNumber(inputValues.contactNumber));

  const handleTermsScroll = (event: React.UIEvent<HTMLDivElement>) => {
    const scrollElement = event.currentTarget;
    const reachedBottom =
      scrollElement.scrollTop + scrollElement.clientHeight >=
      scrollElement.scrollHeight - 8;

    if (reachedBottom) {
      setTermsReachedEnd(true);
    }
  };

  const handlePhoneKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    const allowedControlKeys = [
      "Backspace",
      "Delete",
      "Tab",
      "ArrowLeft",
      "ArrowRight",
      "Home",
      "End",
    ];

    if (
      allowedControlKeys.includes(event.key) ||
      event.ctrlKey ||
      event.metaKey
    ) {
      return;
    }

    if (!/^\d$/.test(event.key)) {
      event.preventDefault();
    }
  };

  const handlePhonePaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
    const pastedText = event.clipboardData.getData("text");

    if (!/^\d+$/.test(pastedText)) {
      event.preventDefault();
    }
  };

  const renderTermsContent = () => {
    const handleScroll = (event: ChangeEvent<HTMLDivElement>) => {
      const { scrollTop, scrollHeight, clientHeight } = event.target;
      if (Math.abs(scrollHeight - clientHeight - scrollTop) < 1) {
        setScrollBottom(true);
      }
    }
    return (
    <RBHBox onScroll={handleScroll} className="invitation-form-section" style={styles.formSection}>
      <RBHBox className="invitation-terms-section" style={styles.termsSection}>
        <RBHText style={styles.sectionTitle}>ข้อกำหนดและเงื่อนไข</RBHText>
        <RBHBox className="invitation-terms-card" style={styles.termsCard}>
          <div
            className="invitation-terms-content"
            ref={termsScrollRef}
            style={styles.termsScroll}
            onScroll={handleTermsScroll}
            dangerouslySetInnerHTML={tncDetail?.th ? {__html: tncDetail.th} : undefined}
          />

          <label style={styles.checkboxRow}>
            <Checkbox
              checked={agreedTerms}
              disabled={!termsReachedEnd}
              onChange={(event) => setAgreedTerms(event.target.checked)}
            />
            <span
              style={
                !termsReachedEnd
                  ? styles.checkboxLabelDisabled
                  : styles.checkboxLabel
              }
            >
              ข้าพเจ้าได้อ่านและยอมรับข้อตกลงและเงื่อนไข
            </span>
          </label>
        </RBHBox>
      </RBHBox>

      <RBHBox style={styles.buttonWrap}>
        <RBHButton
          className="invitation-button"
          id="invitation-accept-terms"
          data-testid="invitation-accept-terms"
          disabled={(!agreedTerms || !scrollBottom) || isSubmitting}
          style={{
            ...styles.submitButton,
            ...(!agreedTerms || isSubmitting
              ? styles.submitButtonDisabled
              : {}),
          }}
          onClick={handleAcceptTerms}
        >
          ยืนยัน
        </RBHButton>
      </RBHBox>
    </RBHBox>
  )};
  const renderSuccessContent = () => (
    <RBHBox
      className="invitation-success-section"
      style={styles.successSection}
    >
      <Lottie
        animationData={successAnimation}
        autoplay
        style={styles.successAnimation}
      />
      <RBHText
        className="invitation-success-title"
        style={styles.sectionSuccessTitle}
      >
        ได้รับข้อมูลของคุณแล้ว
      </RBHText>
      <RBHText
        className="invitation-success-title"
        style={styles.sectionSuccessTitle}
      >
        รอการติดต่อกลับจาก Robinhood ได้เลย
      </RBHText>
      <RBHText
        className="invitation-success-sub-title"
        style={styles.sectionSuccessSubTitle}
      >
        <span>หากมีข้อสงสัยเพิ่มเติมติดต่อ LINE OA</span>{" "}
        <a href='https://line.me/R/ti/p/%40robinhoodshop' style={{ color: Colors.violet }}>@robinhoodshop</a>
      </RBHText>
    </RBHBox>
  );

  const renderFormContent = () => (
    <Form
      className="invitation-form"
      form={form}
      layout="vertical"
      initialValues={{ availableTime: selectedTime }}
      onValuesChange={handleValuesChange}
      onFinish={handleFormSubmit}
      style={styles.form}
    >
      <RBHBox className="invitation-form-section" style={styles.formSection}>
        <RBHBox>
          <RBHText
            data-testid="invitation-section-personal"
            id="invitation-section-personal"
            style={styles.sectionTitle}
          >
            ข้อมูลของคุณ
          </RBHText>
          <RBHBox className="invitation-input-grid" style={styles.inputGrid}>
            <Form.Item
              name="contactName"
              rules={[
                {
                  required: true,
                  message: (
                    <span style={styles.inputRule}>
                      <ExclamationCircleFilled
                        style={styles.exclamationIcon}
                        translate={null}
                      />
                      โปรดระบุชื่อ - นามสกุล
                    </span>
                  ),
                },
              ]}
              style={styles.formItem}
            >
              <RBHBox
                className="invitation-input-wrap"
                style={styles.inputWrap}
              >
                <span
                  style={
                    shouldFloatName
                      ? styles.floatingLabel
                      : styles.customPlaceholder
                  }
                >
                  ชื่อ - นามสกุล*
                </span>

                <RBHTextInput
                  data-testid="invitation-fullname"
                  id="invitation-fullname"
                  prefix={
                    <UserOutlined style={styles.inputPrefix} translate={null} />
                  }
                  style={{
                    ...styles.input,
                    ...(hasNameError ? styles.inputError : {}),
                  }}
                  onFocus={() => setFocusedField("contactName")}
                  onBlur={() => setFocusedField(null)}
                />
              </RBHBox>
            </Form.Item>

            <Form.Item
              name="contactNumber"
              getValueFromEvent={(event) =>
                event?.target?.value?.replace(/\D/g, "").slice(0, 9)
              }
              rules={[
                {
                  required: true,
                  message: (
                    <span style={styles.inputRule}>
                      <ExclamationCircleFilled
                        style={styles.exclamationIcon}
                        translate={null}
                      />
                      โปรดระบุเบอร์ติดต่อกลับ
                    </span>
                  ),
                },
                {
                  validator: (_, value) => {
                    if (!value || isValidPhoneNumber(value)) {
                      return Promise.resolve();
                    }
                    return Promise.reject(
                      <span style={styles.inputRule}>
                        <ExclamationCircleFilled
                          style={styles.exclamationIcon}
                          translate={null}
                        />
                        เบอร์ติดต่อต้องขึ้นต้นด้วย 0 และมี 8-9 หลัก
                      </span>
                    );
                  },
                },
              ]}
              style={styles.formItem}
            >
              <RBHBox
                className="invitation-input-wrap"
                style={styles.inputWrap}
              >
                <span
                  style={
                    shouldFloatPhone
                      ? styles.floatingLabel
                      : styles.customPlaceholder
                  }
                >
                  เบอร์ติดต่อกลับ*
                </span>
                <RBHTextInput
                  data-testid="invitation-phone"
                  id="invitation-phone"
                  maxLength={10}
                  inputMode="numeric"
                  pattern="[0-9]*"
                  prefix={
                    <PhoneOutlined
                      style={styles.inputPrefix}
                      translate={null}
                    />
                  }
                  style={{
                    ...styles.input,
                    ...(hasPhoneError ? styles.inputError : {}),
                  }}
                  onKeyDown={handlePhoneKeyDown}
                  onPaste={handlePhonePaste}
                  onFocus={() => setFocusedField("contactNumber")}
                  onBlur={() => setFocusedField(null)}
                />
              </RBHBox>
            </Form.Item>
          </RBHBox>
        </RBHBox>

        <RBHBox>
          <RBHText
            data-testid="invitation-section-callback"
            id="invitation-section-callback"
            style={styles.sectionTitle}
          >
            ช่วงเวลาที่สะดวกให้ติดต่อกลับ
          </RBHText>
          <RBHText
            data-testid="invitation-section-subtitle"
            id="invitation-section-subtitle"
            style={styles.sectionSubtitle}
          >
            โรบินฮู้ดจะติดต่อกลับในวันทำการ (จันทร์ - ศุกร์)
            ยกเว้นวันหยุดนักขัตฤกษ์
          </RBHText>

          <Form.Item
            name="availableTime"
            rules={[{ required: true, message: "กรุณาเลือกช่วงเวลา" }]}
            style={styles.formItem}
          >
            <Radio.Group
              className="invitation-radio-group"
              value={selectedTime}
              onChange={handleTimeChange}
              style={{ width: "100%" }}
            >
              <RBHBox className="invitation-time-grid" style={styles.timeGrid}>
                {timeOptions.map((timeOption) => (
                  <RBHBox
                    className="invitation-time-card"
                    key={timeOption}
                    style={{
                      ...styles.radioCard,
                      ...(selectedTime === timeOption
                        ? styles.radioCardActive
                        : {}),
                    }}
                    onClick={() =>
                      handleTimeChange({
                        target: { value: timeOption },
                      })
                    }
                  >
                    <RBHRadio
                      value={timeOption}
                      data-testid={`invitation-time-${timeOption}`}
                      id={`invitation-time-${timeOption}`}
                      style={styles.radio}
                    >
                      {timeOption}
                    </RBHRadio>
                  </RBHBox>
                ))}
              </RBHBox>
            </Radio.Group>
          </Form.Item>
        </RBHBox>

        <RBHBox className="invitation-button-wrap" style={styles.buttonWrap}>
          <RBHButton
            id="invitation-submit"
            data-testid="invitation-submit"
            htmlType="submit"
            disabled={formInvalid}
            style={{
              ...styles.submitButton,
              ...(formInvalid ? styles.submitButtonDisabled : {}),
            }}
          >
            ยืนยันการเข้าร่วม
          </RBHButton>
        </RBHBox>
      </RBHBox>
    </Form>
  );

  const renderContent = () => {
    if (tncStatus?.isTncAccepted) {
      return renderSuccessContent();
    }
    if (invitationStep === 'terms') {
      return renderTermsContent();
    } else if (invitationStep === 'success') {
      return renderSuccessContent();
    }

    return renderFormContent();
  }

  return (
    <RBHBox className="invitation-page" style={styles.page}>
      <RBHBox
        className="invitation-page-glow-left"
        style={styles.pageGlowLeft}
      />
      <RBHBox className="invitation-page-glow" style={styles.pageGlowRight1} />
      <RBHBox className="invitation-page-glow" style={styles.pageGlowRight2} />

      <RBHBox className="invitation-content" style={styles.content}>
        <RBHBox className="invitation-card" style={styles.card}>
          <RBHBox className="invitation-hero" style={styles.hero}>
            <RBHImage
              className="invitation-hero-image"
              data-testid="invitation-hero-image"
              id="invitation-hero-image"
              src={channel?.toLowerCase() === 'scbconnect' ? invitationScbHero : invitationHero}
              style={styles.heroImage}
            />
          </RBHBox>
          {renderContent()}
        </RBHBox>
      </RBHBox>
    </RBHBox>
  );
};

type Props = {};
